import React, { useEffect, useState } from "react";
import ChatbotWidget from "./Components/ChatbotWidget.jsx";
import "./Components/ChatbotWidget.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { createPageUrl } from "./src/utils/index.js";
import { TestTube, Plus, List, Settings, Play, Sun, Moon, Home, HelpCircle } from "lucide-react";
import { LogoutHeaderButton } from "./auth/AuthWrapper";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  SidebarProvider,
  SidebarTrigger,
} from "./src/components/ui/sidebar.jsx";

const navigationItems = [
  {
    title: "Home",
    url: "/",
    icon: Home,
  },
  {
    title: "Create Test Case",
    url: createPageUrl("CreateTest"),
    icon: Plus,
  },
  {
    title: "Test Cases",
    url: createPageUrl("TestCases"),
    icon: List,
  },
  {
    title: "Run Evaluations",
    url: createPageUrl("EvaluationRuns"),
    icon: Play,
  },
  {
    title: "Evaluation Results",
    url: createPageUrl("EvaluationResults"),
    icon: List,
  },
];

export default function Layout({ children, currentPageName, onStartTutorial }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem('darkMode');
    return stored === 'true';
  });

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', darkMode);
    localStorage.setItem('darkMode', String(darkMode));
  }, [darkMode]);

  const handleGetHelp = () => {
    navigate('/?help=true');
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-950">
        <style>{`
          :root {
            --primary: 220 70% 15%;
            --primary-foreground: 0 0% 98%;
            --secondary: 158 64% 52%;
            --secondary-foreground: 0 0% 9%;
            --accent: 220 14% 96%;
            --accent-foreground: 220 8% 46%;
            --muted: 220 14% 96%;
            --muted-foreground: 220 8% 46%;
          }
        `}</style>
        
        <Sidebar className="border-r border-slate-200/60 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm">
          <SidebarHeader className="border-b border-slate-200/60 dark:border-slate-800 p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center">
                <img 
                  src="/SVG-08.svg" 
                  alt="EAI Logo" 
                  className="w-10 h-10 object-contain"
                />
              </div>
              <div>
                <h2 className="font-bold text-slate-800 dark:text-slate-100 text-lg">EAI Test Suite</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Evaluation Builder</p>
              </div>
            </div>
          </SidebarHeader>
          
          <SidebarContent className="p-4">
            <SidebarGroup>
              <SidebarGroupLabel className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-3 py-3">
                Navigation
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {navigationItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton 
                        asChild 
                        className={`hover:bg-emerald-50 dark:hover:bg-emerald-900/30 hover:text-emerald-700 dark:hover:text-emerald-300 transition-all duration-200 rounded-xl mb-1 font-medium ${
                          location.pathname === item.url ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 shadow-sm' : ''
                        }`}
                      >
                        <Link
                          to={item.url}
                          className="flex items-center gap-3 px-4 py-3"
                          data-tour={{
                            'Create Test Case': 'create-test-case',
                            'Test Cases': 'test-cases',
                            'Run Evaluations': 'run-evaluations',
                            'Evaluation Results': 'evaluation-results',
                          }[item.title]}
                        >
                          <item.icon className="w-5 h-5" />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>

          <SidebarFooter className="border-t border-slate-200/60 dark:border-slate-800 p-6">
            <div className="text-xs text-slate-500 dark:text-slate-400" data-tour="settings">
              Test Environment
            </div>
          </SidebarFooter>
        </Sidebar>

  <main className="flex-1 flex flex-col" style={{ position: 'relative' }}>
          <header className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm border-b border-slate-200/60 dark:border-slate-800 px-6 py-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <SidebarTrigger className="hover:bg-slate-100 dark:hover:bg-slate-800 p-2 rounded-xl transition-colors duration-200 md:hidden" />
                <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">EAI Test Suite</h1>
              </div>
              <div className="flex items-center gap-2" data-tour="settings">
                <button
                  type="button"
                  aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                  onClick={() => setDarkMode((v) => !v)}
                  className="inline-flex items-center justify-center h-9 w-9 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  {darkMode ? (
                    <Sun className="w-5 h-5 text-slate-700 dark:text-slate-200" />
                  ) : (
                    <Moon className="w-5 h-5 text-slate-700 dark:text-slate-200" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleGetHelp}
                  className="px-3 py-2 text-xs font-medium rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 flex items-center gap-2"
                >
                  <HelpCircle className="w-4 h-4" />
                  Get Help
                </button>
                <LogoutHeaderButton className="px-3 py-2 text-xs font-medium rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200" />
              </div>
            </div>
          </header>

          <div className="flex-1 overflow-auto">
            {children}
            <ChatbotWidget />
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}